import {useState} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {InfoBox, Input} from '@/components';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import Modal from '@/components/Modals';
import type {ArbitrageCheckInfo, RatesMap} from '@/types';

import {ArbitragePairwiseReporter} from './ArbitragePairwiseReporter';
import {ArbitrageReporter} from './ArbitrageReporter';

type ArbitrageDisplayProps = {
  isOpen: boolean;
  arbitrageResult: ArbitrageCheckInfo;
  rates?: RatesMap;
  tolerance: number;
  onToleranceChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClose?: () => void;
};

type ToleranceInputProps = {
  tolerance: number;
  onToleranceChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

const ToleranceInput = ({
  tolerance,
  onToleranceChange,
}: ToleranceInputProps) => (
  <div className="w-full flex justify-end">
    <div>
      <label
        htmlFor="tolerance"
        className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
      >
        Percentage Tolerance
      </label>
      <Input
        type="number"
        id="tolerance"
        className="mb-3 w-full p-2.5"
        placeholder="Enter Tolerance"
        dataCy="rates-arbitrage-tolerance"
        defaultValue={tolerance}
        onChange={onToleranceChange}
        max={100}
        min={0}
      />
    </div>
  </div>
);

const ArbitrageDisplay = ({
  arbitrageResult,
  onToleranceChange,
  tolerance,
  isOpen,
  onClose,
}: ArbitrageDisplayProps) => {
  const [showArbitrageModal, setShowArbitrageModal] = useState(isOpen);

  if (!arbitrageResult.hasArbitrage) {
    return (
      <div className="p-3">
        <InfoBox>
          No arbitrage detected using the tolerance of {tolerance}%
        </InfoBox>
        <PermissionsProvider permission={AfriexAdminPermission.RatesUpload}>
          {onToleranceChange && (
            <ToleranceInput
              tolerance={tolerance}
              onToleranceChange={onToleranceChange}
            />
          )}
        </PermissionsProvider>
      </div>
    );
  }

  const closeModal = () => {
    if (onClose) {
      onClose();
    }
    setShowArbitrageModal(false);
  };

  return (
    <Modal
      isOpen={showArbitrageModal}
      onClose={closeModal}
      width="w-5/6 md:w-4/6 lg:w-6/12"
      height="h-3/5"
      title="ℹ️ Arbitrage Detected"
      zIndex={20}
      closeOnOutsideClick
    >
      <div className="flex flex-col items-center gap-2 p-3">
        <PermissionsProvider permission={AfriexAdminPermission.RatesUpload}>
          {onToleranceChange && (
            <ToleranceInput
              tolerance={tolerance}
              onToleranceChange={onToleranceChange}
            />
          )}
        </PermissionsProvider>
        <div className="flex flex-col p-4 mb-4 text-sm rounded-lg text-red-800  border border-gray-300 dark:border-dark-borderHeavy">
          <div className="ml-2">
            {arbitrageResult.cycles?.map((cycle, index) => (
              <ArbitrageReporter key={index} {...cycle} tolerance={tolerance} />
            ))}
            <ArbitragePairwiseReporter
              {...arbitrageResult.pairWiseDiscrepancy}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ArbitrageDisplay;
